import React from 'react';
import { 
  Shield, TrendingUp, TrendingDown, Sparkles, 
  CheckCircle2, AlertCircle, Activity, Users,
  Target, Brain, ClipboardCheck, Zap, Flame,
  AlertTriangle, ChevronRight, Radio, Building2
} from 'lucide-react';
import { DESIGN } from '../DesignSystem';

// Chief of Staff Premium Colors - Royal Purple Theme
const COS_PULSE_COLORS = {
  primary: '#7B4FE8',
  primaryDark: '#5E35B1',
  primaryGlow: 'rgba(123, 79, 232, 0.4)',
  primarySubtle: 'rgba(123, 79, 232, 0.08)',
  heroBg: '#1A1428',
  heroGradient: 'linear-gradient(90deg, rgba(123, 79, 232, 0.2) 0%, transparent 60%)',
  cardBorder: 'rgba(123, 79, 232, 0.25)',
  surfaceDark: '#1E1B2E',
  accentGreen: '#10B981',
  accentAmber: '#F59E0B',
  accentRed: '#EF4444',
  accentBlue: '#3B82F6'
};

// Unique IP Colors for Soul Food section
const UNIQUE_IP_COLORS = {
  primary: '#F59E0B',
  primarySubtle: 'rgba(245, 158, 11, 0.1)'
};

// ================================================
// SPARKLINE CHART COMPONENT
// ================================================
const Sparkline = ({ data, color, width = 60, height = 24, trending }) => {
  if (!data || data.length === 0) return null;
  
  const max = Math.max(...data);
  const min = Math.min(...data);
  const range = max - min || 1;
  
  const points = data.map((value, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((value - min) / range) * (height - 4) - 2;
    return `${x},${y}`;
  }).join(' ');
  
  const trendColor = trending === 'up-bad' ? COS_PULSE_COLORS.accentRed :
                     trending === 'up-good' ? COS_PULSE_COLORS.accentGreen :
                     trending === 'down-good' ? COS_PULSE_COLORS.accentGreen :
                     trending === 'down-bad' ? COS_PULSE_COLORS.accentRed : color;
  
  return (
    <svg width={width} height={height} className="overflow-visible">
      <polyline
        points={points}
        fill="none"
        stroke={trendColor}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{ filter: `drop-shadow(0 0 3px ${trendColor}50)` }}
      />
      <circle
        cx={width}
        cy={height - ((data[data.length - 1] - min) / range) * (height - 4) - 2}
        r="3"
        fill={trendColor}
        style={{ filter: `drop-shadow(0 0 4px ${trendColor})` }}
      />
    </svg>
  );
};

// ================================================
// PREMIUM COMMAND CENTER HERO BANNER
// ================================================
export const PulseCommandCenterHero = ({ momentum, scope }) => {
  const pulseIndicators = [
    { label: 'Executive Response Active', status: 'active', color: COS_PULSE_COLORS.accentGreen },
    { label: '2 Escalations Pending', status: 'warning', color: COS_PULSE_COLORS.accentAmber },
    { label: 'Board Prep Due in 4h', status: 'urgent', color: COS_PULSE_COLORS.accentRed }
  ];

  return (
    <div 
      className="relative w-full rounded-2xl overflow-hidden"
      data-testid="cos-pulse-hero"
      style={{ 
        backgroundColor: COS_PULSE_COLORS.heroBg,
        boxShadow: `0 8px 32px rgba(0,0,0,0.4), 0 0 80px ${COS_PULSE_COLORS.primaryGlow}`
      }}
    >
      {/* Purple gradient from left */}
      <div 
        className="absolute inset-0"
        style={{ background: COS_PULSE_COLORS.heroGradient }}
      />
      
      {/* Animated grid pattern */}
      <div 
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: `linear-gradient(rgba(123, 79, 232, 0.4) 1px, transparent 1px),
                           linear-gradient(90deg, rgba(123, 79, 232, 0.4) 1px, transparent 1px)`,
          backgroundSize: '40px 40px'
        }}
      />

      {/* Subtle radial highlight */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse at 20% 50%, rgba(123, 79, 232, 0.15) 0%, transparent 50%)'
        }}
      />
      
      <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between p-8 gap-6">
        {/* Left side - Icon, Title, and Momentum */}
        <div className="flex items-center gap-6">
          {/* Glowing shield icon */}
          <div className="relative">
            <div 
              className="absolute inset-0 rounded-full blur-xl animate-pulse"
              style={{ 
                backgroundColor: COS_PULSE_COLORS.primary,
                opacity: 0.4,
                transform: 'scale(1.6)'
              }}
            />
            <div 
              className="relative w-20 h-20 rounded-full flex items-center justify-center"
              style={{ 
                backgroundColor: COS_PULSE_COLORS.primary,
                boxShadow: `0 0 40px ${COS_PULSE_COLORS.primaryGlow}, inset 0 2px 12px rgba(255,255,255,0.25)`
              }}
            >
              <Shield className="w-10 h-10 text-white" />
            </div>
          </div>
          
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-3xl font-black text-white tracking-tight">
                Coordination Pulse
              </h1>
              <span 
                className="px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider"
                style={{ 
                  backgroundColor: COS_PULSE_COLORS.primary,
                  color: 'white',
                  boxShadow: `0 0 15px ${COS_PULSE_COLORS.primaryGlow}`
                }}
              >
                Live
              </span>
            </div>
            <p className="text-lg font-medium" style={{ color: 'rgba(167, 139, 250, 0.9)' }}>
              {momentum?.headline || 'Organization executing well — 4 items need your attention today'}
            </p>
            <p className="text-sm mt-1" style={{ color: 'rgba(167, 139, 250, 0.6)' }}>
              {momentum?.supportingText || 'Cross-departmental coordination active across all divisions'}
            </p>
          </div>
        </div>
        
        {/* Right side - Live pulse indicators */}
        <div className="flex flex-col gap-2">
          {pulseIndicators.map((indicator, idx) => (
            <div 
              key={idx}
              className="flex items-center gap-3 px-4 py-2.5 rounded-xl"
              style={{ 
                backgroundColor: 'rgba(255,255,255,0.06)',
                border: `1px solid ${indicator.color}30`
              }}
            >
              {/* Animated pulse dot */}
              <div className="relative">
                <div 
                  className="w-2.5 h-2.5 rounded-full"
                  style={{ backgroundColor: indicator.color }}
                />
                <div 
                  className="absolute inset-0 w-2.5 h-2.5 rounded-full animate-ping"
                  style={{ backgroundColor: indicator.color, opacity: 0.5 }}
                />
              </div>
              <span className="text-sm font-medium text-white whitespace-nowrap">
                {indicator.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ================================================
// PREMIUM STAT CARD WITH SPARKLINE (For Pulse)
// ================================================
export const PulseStatCard = ({ label, value, unit, trend, trendValue, sparklineData, trendDirection, icon: Icon }) => {
  const getTrendColor = () => {
    if (trendDirection === 'down-is-good') {
      return trend === 'down' ? COS_PULSE_COLORS.accentGreen : COS_PULSE_COLORS.accentRed;
    }
    if (trendDirection === 'up-is-bad') {
      return trend === 'up' ? COS_PULSE_COLORS.accentRed : COS_PULSE_COLORS.accentGreen;
    }
    return trend === 'up' ? COS_PULSE_COLORS.accentGreen : 
           trend === 'down' ? COS_PULSE_COLORS.accentRed : 
           DESIGN.colors.textMuted;
  };

  const sparklineTrend = trendDirection === 'down-is-good' 
    ? (trend === 'down' ? 'down-good' : 'up-bad')
    : trendDirection === 'up-is-bad'
      ? (trend === 'up' ? 'up-bad' : 'down-good')
      : (trend === 'up' ? 'up-good' : 'down-bad');

  return (
    <div 
      className="relative p-5 rounded-xl overflow-hidden group transition-all duration-300 hover:scale-[1.02]"
      style={{ 
        backgroundColor: DESIGN.colors.card,
        borderTop: `4px solid ${COS_PULSE_COLORS.primary}`,
        boxShadow: DESIGN.shadows.md
      }}
    >
      {/* Subtle corner glow */}
      <div 
        className="absolute top-0 right-0 w-24 h-24 opacity-20 group-hover:opacity-30 transition-opacity"
        style={{
          background: `radial-gradient(circle at top right, ${COS_PULSE_COLORS.primary} 0%, transparent 70%)`
        }}
      />

      <div className="flex justify-between items-start relative">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            {Icon && (
              <div 
                className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: COS_PULSE_COLORS.primarySubtle }}
              >
                <Icon className="w-4 h-4" style={{ color: COS_PULSE_COLORS.primary }} />
              </div>
            )}
            <p className="text-sm font-semibold" style={{ color: DESIGN.colors.textSecondary }}>
              {label}
            </p>
          </div>
          <div className="flex items-baseline gap-2">
            <span 
              className="text-4xl font-black"
              style={{ color: DESIGN.colors.textPrimary }}
            >
              {value}{unit === '%' ? unit : ''}
            </span>
            {trendValue !== undefined && (
              <span 
                className="flex items-center gap-1 text-sm font-bold"
                style={{ color: getTrendColor() }}
              >
                {trend === 'up' ? <TrendingUp className="w-4 h-4" /> : 
                 trend === 'down' ? <TrendingDown className="w-4 h-4" /> : null}
                {Math.abs(trendValue)}
              </span>
            )}
          </div>
        </div>
        
        {/* Sparkline in bottom right */}
        {sparklineData && (
          <div className="absolute bottom-3 right-3">
            <Sparkline 
              data={sparklineData} 
              color={COS_PULSE_COLORS.primary}
              width={70}
              height={32}
              trending={sparklineTrend}
            />
          </div>
        )}
      </div>
    </div>
  );
};

// ================================================
// WORK ADVANCING FEED (Premium Version)
// ================================================
export const PulseWorkAdvancingFeed = ({ items }) => {
  const statusConfig = {
    completed: { icon: CheckCircle2, color: COS_PULSE_COLORS.accentGreen, bg: 'rgba(16, 185, 129, 0.1)' },
    'in-progress': { icon: Activity, color: COS_PULSE_COLORS.accentBlue, bg: 'rgba(59, 130, 246, 0.1)' },
    escalated: { icon: AlertTriangle, color: COS_PULSE_COLORS.accentAmber, bg: 'rgba(245, 158, 11, 0.1)' },
    urgent: { icon: Flame, color: COS_PULSE_COLORS.accentRed, bg: 'rgba(239, 68, 68, 0.1)' }
  };

  return (
    <div 
      className="p-6 rounded-2xl"
      style={{ 
        backgroundColor: DESIGN.colors.card, 
        boxShadow: DESIGN.shadows.lg,
        border: `1px solid ${COS_PULSE_COLORS.cardBorder}`
      }}
    >
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-lg font-bold flex items-center gap-3" style={{ color: DESIGN.colors.textPrimary }}>
          <div 
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ 
              background: `linear-gradient(135deg, ${COS_PULSE_COLORS.primary} 0%, ${COS_PULSE_COLORS.primaryDark} 100%)`,
              boxShadow: `0 0 20px ${COS_PULSE_COLORS.primaryGlow}`
            }}
          >
            <Activity className="w-5 h-5 text-white" />
          </div>
          Work Advancing
        </h3>
        <div className="flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ backgroundColor: 'rgba(16, 185, 129, 0.1)' }}>
          <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
          <span className="text-xs font-bold" style={{ color: COS_PULSE_COLORS.accentGreen }}>Live</span>
        </div>
      </div>
      
      <div className="space-y-3">
        {items?.map(item => {
          const config = statusConfig[item.status] || statusConfig.completed;
          const Icon = config.icon;
          return (
            <div 
              key={item.id}
              className="flex items-start gap-4 p-4 rounded-xl transition-all hover:shadow-md cursor-pointer group"
              style={{ 
                backgroundColor: DESIGN.colors.background,
                borderLeft: `3px solid ${item.entity === 'soulfood' ? UNIQUE_IP_COLORS.primary : COS_PULSE_COLORS.primary}`
              }}
            >
              <div 
                className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110"
                style={{ backgroundColor: config.bg }}
              >
                <Icon className="w-5 h-5" style={{ color: config.color }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold" style={{ color: DESIGN.colors.textPrimary }}>
                  {item.title}
                </p>
                <div className="flex items-center gap-3 mt-1.5">
                  <span className="text-xs" style={{ color: DESIGN.colors.textMuted }}>
                    {item.timestamp}
                  </span>
                  <span 
                    className="text-xs px-2.5 py-1 rounded-full font-bold"
                    style={{ 
                      backgroundColor: item.entity === 'soulfood' 
                        ? UNIQUE_IP_COLORS.primarySubtle 
                        : COS_PULSE_COLORS.primarySubtle,
                      color: item.entity === 'soulfood' 
                        ? UNIQUE_IP_COLORS.primary 
                        : COS_PULSE_COLORS.primary
                    }}
                  >
                    {item.entity === 'soulfood' ? 'Soul Food' : 'Company'}
                  </span>
                </div>
              </div>
              <ChevronRight 
                className="w-5 h-5 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                style={{ color: DESIGN.colors.textMuted }}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ================================================
// SOUL FOOD DIVISION HEALTH (Premium Card)
// ================================================
export const SoulFoodDivisionHealth = ({ health }) => {
  return (
    <div 
      className="p-6 rounded-2xl"
      style={{ 
        backgroundColor: DESIGN.colors.card, 
        boxShadow: DESIGN.shadows.lg,
        border: `1px solid rgba(245, 158, 11, 0.25)`
      }}
    >
      <div className="flex items-center gap-3 mb-5">
        <div 
          className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ 
            background: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)',
            boxShadow: '0 0 20px rgba(245, 158, 11, 0.4)'
          }}
        >
          <Sparkles className="w-5 h-5 text-white" />
        </div>
        <h3 className="text-lg font-bold" style={{ color: DESIGN.colors.textPrimary }}>
          Soul Food Division
        </h3>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        {[
          { label: 'Velocity', value: health?.velocity || 28, color: '#10B981' },
          { label: 'Active Initiatives', value: health?.activeInitiatives || 6, color: '#3B82F6' },
          { label: 'Brand Health', value: `${health?.brandHealth || 87}%`, color: '#F59E0B' },
          { 
            label: 'Needs Attention', 
            value: health?.itemsNeedingExecutiveAttention || 1,
            color: (health?.itemsNeedingExecutiveAttention || 1) > 0 ? '#F59E0B' : '#10B981',
            alert: (health?.itemsNeedingExecutiveAttention || 1) > 0
          }
        ].map((metric, idx) => (
          <div 
            key={idx}
            className="p-4 rounded-xl text-center transition-all hover:scale-105"
            style={{ 
              backgroundColor: metric.alert ? 'rgba(245, 158, 11, 0.1)' : UNIQUE_IP_COLORS.primarySubtle,
              border: metric.alert ? '1px solid rgba(245, 158, 11, 0.3)' : 'none'
            }}
          >
            <p 
              className="text-2xl font-black"
              style={{ 
                color: metric.color,
                textShadow: metric.alert ? `0 0 15px ${metric.color}40` : 'none'
              }}
            >
              {metric.value}
            </p>
            <p className="text-xs font-medium mt-1" style={{ color: DESIGN.colors.textSecondary }}>
              {metric.label}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
};

// ================================================
// COGNITIVE LANES OVERVIEW (Premium Grid)
// ================================================
export const CognitiveLanesOverview = ({ lanes }) => {
  return (
    <div 
      className="p-6 rounded-2xl"
      style={{ 
        backgroundColor: DESIGN.colors.card, 
        boxShadow: DESIGN.shadows.lg,
        border: `1px solid ${COS_PULSE_COLORS.cardBorder}`
      }}
    >
      <div className="flex items-center gap-3 mb-5">
        <div 
          className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ 
            background: `linear-gradient(135deg, ${COS_PULSE_COLORS.primary} 0%, ${COS_PULSE_COLORS.primaryDark} 100%)`,
            boxShadow: `0 0 20px ${COS_PULSE_COLORS.primaryGlow}`
          }}
        >
          <Target className="w-5 h-5 text-white" />
        </div>
        <h3 className="text-lg font-bold" style={{ color: DESIGN.colors.textPrimary }}>
          All Cognitive Lanes
        </h3>
      </div>
      
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {lanes?.map((lane, idx) => {
          const healthColor = lane.health === 'attention' ? '#EF4444' : 
                              lane.health === 'elevated' ? '#F59E0B' : '#10B981';
          return (
            <div 
              key={idx}
              className="p-4 rounded-xl transition-all hover:shadow-md cursor-pointer group"
              style={{ 
                backgroundColor: DESIGN.colors.background,
                borderLeft: `4px solid ${lane.color}`
              }}
            >
              <div className="flex items-center gap-2 mb-2">
                <div 
                  className="w-3 h-3 rounded-full"
                  style={{ 
                    backgroundColor: lane.color,
                    boxShadow: `0 0 8px ${lane.color}60`
                  }}
                />
                <span 
                  className="text-sm font-semibold truncate" 
                  style={{ color: DESIGN.colors.textPrimary }}
                >
                  {lane.lane_name || lane.lane}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span 
                  className="text-xl font-black"
                  style={{ color: healthColor }}
                >
                  {Math.round((lane.on_time_percentage || 0.8) * 100)}%
                </span>
                <div className="flex items-center gap-1">
                  {lane.velocity_trend === 'up' 
                    ? <TrendingUp className="w-4 h-4" style={{ color: '#10B981' }} />
                    : <TrendingDown className="w-4 h-4" style={{ color: '#EF4444' }} />
                  }
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ================================================
// EXECUTIVE REVIEW PIPELINE (Premium Version)
// ================================================
export const ExecutiveReviewPipeline = ({ items }) => {
  return (
    <div 
      className="p-6 rounded-2xl"
      style={{ 
        background: `linear-gradient(135deg, ${COS_PULSE_COLORS.surfaceDark} 0%, #2D2640 100%)`,
        boxShadow: `0 8px 32px rgba(0,0,0,0.3), 0 0 40px ${COS_PULSE_COLORS.primaryGlow}`,
        border: `2px solid ${COS_PULSE_COLORS.primary}40`
      }}
    >
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-lg font-bold flex items-center gap-3 text-white">
          <div 
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ 
              backgroundColor: COS_PULSE_COLORS.primary,
              boxShadow: `0 0 20px ${COS_PULSE_COLORS.primaryGlow}`
            }}
          >
            <ClipboardCheck className="w-5 h-5 text-white" />
          </div>
          Executive Review Pipeline
        </h3>
        <span 
          className="px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider"
          style={{ 
            backgroundColor: COS_PULSE_COLORS.primary,
            color: 'white',
            boxShadow: `0 0 15px ${COS_PULSE_COLORS.primaryGlow}`
          }}
        >
          Primary
        </span>
      </div>
      
      <div className="space-y-3">
        {items?.map(item => {
          const slaColor = item.sla_status === 'at_risk' ? '#EF4444' : 
                          item.sla_status === 'warning' ? '#F59E0B' : '#10B981';
          return (
            <div 
              key={item.id}
              className="flex items-center justify-between p-4 rounded-xl transition-all hover:scale-[1.01]"
              style={{ 
                backgroundColor: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.08)'
              }}
            >
              <div className="flex-1">
                <p className="font-semibold text-white">{item.name}</p>
                <div className="flex items-center gap-3 mt-1">
                  <span className="text-xs" style={{ color: 'rgba(167, 139, 250, 0.7)' }}>
                    {item.type} • By {item.submitted_by}
                  </span>
                  <span className="text-xs" style={{ color: 'rgba(167, 139, 250, 0.5)' }}>
                    {item.content_summary}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <span 
                    className="text-lg font-black"
                    style={{ 
                      color: slaColor,
                      textShadow: item.sla_status === 'at_risk' ? `0 0 10px ${slaColor}60` : 'none'
                    }}
                  >
                    {item.hours_pending}h
                  </span>
                  <p className="text-xs" style={{ color: 'rgba(167, 139, 250, 0.5)' }}>pending</p>
                </div>
                <button 
                  className="px-4 py-2 rounded-lg text-sm font-bold transition-all hover:scale-105"
                  style={{ 
                    backgroundColor: COS_PULSE_COLORS.primary,
                    color: 'white',
                    boxShadow: `0 0 15px ${COS_PULSE_COLORS.primaryGlow}`
                  }}
                >
                  Follow Up
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Export colors for use in parent component
export { COS_PULSE_COLORS };
